import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

// Shared across all requests so simultaneous 401s trigger only ONE refresh call.
// null token emitted -> refresh failed (callers should surface the error).
let isRefreshing = false;
const refreshDone$ = new Subject<string | null>();

const isAuthEndpoint = (url: string) =>
  url.includes('/Identity/authenticate') || url.includes('/Identity/RefreshToken');

const withToken = (req: HttpRequest<unknown>, token: string) =>
  req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

/**
 * Attaches the bearer token to same-origin API calls and transparently refreshes
 * an expired token on a 401. A single in-flight refresh is shared across
 * concurrent requests; if refresh fails the session is cleared and the user is
 * sent to /login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const token = auth.getToken();

  const isApiCall = req.url.startsWith(environment.apiBase);
  const authEndpoint = isAuthEndpoint(req.url);

  const authReq =
    token && isApiCall && !authEndpoint ? withToken(req, token) : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      // Only attempt refresh for 401s on real API calls (never on login/refresh itself).
      if (err.status === 401 && isApiCall && !authEndpoint && auth.getToken()) {
        return handle401(req, next, auth, router);
      }
      if ((err.status === 401 || err.status === 403) && !authEndpoint) {
        auth.logout();
        router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  router: Router,
): Observable<any> {
  if (isRefreshing) {
    // A refresh is already in flight — wait for its result, then retry.
    return refreshDone$.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap((newToken) => next(withToken(req, newToken))),
    );
  }

  isRefreshing = true;
  return auth.refreshToken().pipe(
    switchMap((newToken) => {
      isRefreshing = false;
      refreshDone$.next(newToken);
      if (newToken) {
        return next(withToken(req, newToken));
      }
      // Refresh failed -> force logout.
      auth.logout();
      router.navigate(['/login']);
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }),
    catchError((e) => {
      isRefreshing = false;
      refreshDone$.next(null);
      auth.logout();
      router.navigate(['/login']);
      return throwError(() => e);
    }),
  );
}
